'use client';

import { useEffect } from 'react';

import { isNativeApp } from 'utils/nativeOAuth';
import { openExternalUrl } from 'utils/openExternalUrl';

/**
 * Global external-link handler for the native app.
 *
 * Inside the Capacitor app, any `target="_blank"` or cross-origin http(s) link
 * would otherwise kick the user out to the external Safari app — which App Store
 * Guideline 4 flags (web content must stay in-app). This mounts one capturing
 * click listener that reroutes those links through the in-app Safari View
 * Controller (via openExternalUrl → @capacitor/browser).
 *
 * Left untouched: internal same-origin links (Next.js SPA routing) and
 * non-http schemes like mailto:/tel: (handled by the OS). No-op on web.
 *
 * Mounted once, globally, in _app.tsx.
 */
export default function NativeExternalLinks() {
  useEffect(() => {
    if (!isNativeApp()) return undefined;

    const onClick = (event: MouseEvent) => {
      // ignore modified clicks / non-primary buttons
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey) return;

      const anchor = (event.target as HTMLElement)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!anchor) return;

      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('#')) return;

      let url: URL;
      try {
        url = new URL(href, window.location.href);
      } catch {
        return;
      }

      const isHttp = url.protocol === 'http:' || url.protocol === 'https:';
      if (!isHttp) return; // let mailto:, tel:, custom schemes fall through to the OS

      const isBlank = anchor.getAttribute('target') === '_blank';
      const isCrossOrigin = url.origin !== window.location.origin;

      // Only hijack links that would otherwise leave the app for external Safari.
      if (isBlank || isCrossOrigin) {
        event.preventDefault();
        openExternalUrl(url.href);
      }
    };

    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);

  return null;
}
